import React from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import Lottie from 'react-lottie';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { getFirstName } from '@/components/signup/utils/get-first-name';
import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { AIFooter } from './components/ai-footer';
import { ColdStartButtonsRow } from './components/cold-starts-buttons';
import rovoLogoLoader from './components/logos/rovo-logo-loader.json';

const sectionStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});
const chatHeaderContainerStyles = css({
	maxWidth: '768px',
	width: '100%',
	textAlign: 'center',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '24px !important',
	rowGap: '24px !important',
	columnGap: '24px !important',
	marginBlockEnd: '24px',
});

const rovoLoaderStyles = css({
	width: '36px !important',
	height: '36px !important',
	minWidth: '36px',
	minHeight: '36px',
	maxWidth: '36px',
	maxHeight: '36px',
	margin: '0 !important',
	padding: '0 !important',
	display: 'block',
	overflow: 'hidden',
});

const h1Styles = css({
	padding: 0,
	margin: 0,
	lineHeight: '56px',
	fontSize: '48px',
	fontWeight: '800',
});

const textfieldContainerStyles = css({
	display: 'flex',
	width: 'min(700px, 100%)',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.150'),
	boxSizing: 'border-box',

	borderRadius: '12px',
	border: `1px solid ${wacToken('color.neutral.light300')}`,
	background: token('color.background.input'),
	boxShadow: token('elevation.shadow.overlay'),
	padding: `${token('space.200')} ${token('space.200')} ${token(
		'space.150',
	)} ${token('space.200')}`,
	[media.below.xs]: {
		padding: `${token('space.150')}`,
	},
	[media.above.sm]: {
		width: '700px',
	},
	overflow: 'hidden',
});

const ctaButtonStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	width: '100%',
	gap: token('space.100'),
});

type RovoPillButtonProps = {
	isDisabled?: boolean;
	onClick?: () => void;
};

const disabledInteractionCss = css({
	pointerEvents: 'none',
	cursor: 'default',
});

const RovoPillButton: React.FC<RovoPillButtonProps> = ({ isDisabled, onClick }) => {
	const intl = useIntl();
	const askRovoLabel = intl.formatMessage({
		id: 'RovoChat.askRovo',
		defaultMessage: 'Ask Rovo',
	});
	const tooltipContent = intl.formatMessage({
		id: 'RovoChat.writePromptTooltip',
		defaultMessage: 'Write a prompt',
	});

	const button = (
		<Button
			css={[css({ minHeight: '40px' }), isDisabled ? disabledInteractionCss : null]}
			variant="primary"
			size="small"
			onClick={isDisabled ? undefined : onClick}
			dataEventProps={{
				'data-product-key': 'rovo',
				'data-event': 'clicked',
				'data-event-component': 'linkButton',
				'data-event-container': 'rovoChatBox',
			}}
		>
			{askRovoLabel}
		</Button>
	);

	// Only show tooltip when button is disabled (no text entered)
	if (isDisabled) {
		return (
			<Tooltip content={tooltipContent}>
				<span style={{ display: 'inline-block' }}>{button}</span>
			</Tooltip>
		);
	}

	// No tooltip when button is enabled (user has typed something)
	return button;
};

const inputStyles = css({
	width: '100%',
	minHeight: '20px',
	maxHeight: '140px',
	overflowY: 'auto',
	border: 'none',
	background: 'transparent',
	padding: 0,
	fontSize: '16px',
	fontFamily: 'inherit',
	lineHeight: '1.5',
	wordWrap: 'break-word',
	overflowWrap: 'break-word',
	whiteSpace: 'pre-wrap',
	resize: 'none',
	'&:focus': {
		outline: 'none',
	},
});
const chatBoxContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	maxWidth: '700px',
	width: '100%',
	gap: '8px',
});

const getNamedHeader = (firstName: string) => {
	return (
		<FormattedMessage
			id="RovoChat.greeting"
			defaultMessage="Hey {name},{br}let's get to work"
			values={{
				name: getFirstName(firstName),
				br: <br />,
			}}
		/>
	);
};
export const ChatHeader: React.FC = () => {
	const { isUserLoggedIn, name } = useLoggedInUser();
	const firstName = getFirstName(name ?? '');

	// If 'gwp-9000-new-rovo-video-hero' is enabled, <h1> shows the video; else, Rovo Chat Header uses <h1>.
	// Check feature flag at render time to allow tests to mock it properly
	const isNewRovoVideoHeroEnabled = checkFeatureFlagGate(featureFlags.gwp9000NewRovoVideoHero);

	const getMessage = () => {
		if (isUserLoggedIn && firstName) {
			return getNamedHeader(firstName);
		}
		return (
			<FormattedMessage
				id="RovoChat.greeting"
				defaultMessage="Hey there,{br}ready to get to work?"
				values={{
					br: <br />,
				}}
			/>
		);
	};

	return (
		<div css={chatHeaderContainerStyles}>
			<div css={rovoLoaderStyles}>
				<Lottie
					options={{
						loop: true,
						autoplay: true,
						animationData: rovoLogoLoader,
						rendererSettings: {
							preserveAspectRatio: 'xMidYMid slice',
						},
					}}
					height={36}
					width={36}
				/>
			</div>
			{isNewRovoVideoHeroEnabled ? (
				<h2 css={h1Styles}>{getMessage()}</h2>
			) : (
				<h1 css={h1Styles}>{getMessage()}</h1>
			)}
		</div>
	);
};

type RovoChatBoxProps = {
	textareaRef?: React.Ref<HTMLTextAreaElement>;
	value?: string;
	onValueChange?: (value: string) => void;
	previewValue?: string;
};

export const RovoChatBox: React.FC<RovoChatBoxProps> = ({
	textareaRef: externalRef,
	value: externalValue,
	onValueChange,
	previewValue,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const hasTrackedTyping = React.useRef(false);

	// Attribution for the productionized `gwp-8979-rovo-chat-on-wac` experiment.
	useFeatureExposed('gwp-8979-rovo-chat-on-wac', 'Test');

	const defaultPlaceholder = intl.formatMessage({
		id: 'RovoChat.placeholder',
		defaultMessage: 'Find something, plan something, or make something',
	});
	const placeholder = previewValue || defaultPlaceholder;

	const internalRef = React.useRef<HTMLTextAreaElement>(null);
	const textareaRef = externalRef || internalRef;
	const MAX_HEIGHT = 140; // keep in sync with inputStyles.maxHeight

	const resizeToContent = React.useCallback(() => {
		const el = (textareaRef as React.RefObject<HTMLTextAreaElement>).current;
		if (!el) return;
		el.style.height = 'auto';
		const next = Math.min(el.scrollHeight, MAX_HEIGHT);
		el.style.height = `${next}px`;
	}, [textareaRef, MAX_HEIGHT]);

	React.useEffect(() => {
		resizeToContent();
	}, [resizeToContent]);

	const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = () => {
		resizeToContent();
	};

	const [value, setValue] = React.useState('');
	const displayValue = externalValue ?? value;

	const fireTypingAnalytics = React.useCallback(() => {
		if (!hasTrackedTyping.current && analyticsClient) {
			hasTrackedTyping.current = true;
			createAnalyticsEvent({
				product: 'marketing',
				platform: 'web',
				eventType: 'ui',
				action: 'typed',
				actionSubject: 'textInput',
				actionSubjectId: 'rovoChatBox',
				attributes: {
					url: location.href,
				},
			}).fire();
		}
	}, [analyticsClient, createAnalyticsEvent]);

	const handleClick = () => {
		const prompt = displayValue.trim();
		if (!prompt) return;

		// Fire analytics event for submission
		if (analyticsClient) {
			createAnalyticsEvent({
				product: 'marketing',
				platform: 'web',
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'linkButton',
				actionSubjectId: 'rovoChatBox',
				attributes: {
					url: location.href,
					promptLength: prompt.length,
				},
			}).fire();
		}

		const chatUrl = new URL('https://home.atlassian.com/chat');
		chatUrl.searchParams.set('rovoChatPathway', 'chat');
		chatUrl.searchParams.set('rovoChatPrompt', prompt);

		const loginUrl = `https://id.atlassian.com/login?continue=${encodeURIComponent(
			chatUrl.toString(),
		)}`;

		console.log('generated URL: ', loginUrl);
		window.location.assign(loginUrl);
	};

	const isButtonDisabled = !displayValue.trim();

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Submit on Enter (but allow Shift+Enter for new lines)
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleClick();
		}
	};

	return (
		<div css={textfieldContainerStyles}>
			<textarea
				ref={textareaRef}
				css={inputStyles}
				tabIndex={0}
				placeholder={placeholder}
				maxLength={1000}
				value={displayValue}
				onChange={(e) => {
					const newValue = e.target.value;
					setValue(newValue);
					onValueChange?.(newValue);
					handleChange(e);

					// Fire analytics event on first character typed
					if (newValue.length > 0) {
						fireTypingAnalytics();
					}
				}}
				onKeyDown={handleKeyDown}
			/>
			<div css={ctaButtonStyles}>
				<RovoPillButton isDisabled={isButtonDisabled} onClick={handleClick} />
			</div>
		</div>
	);
};

export const RovoChatBoxContainer: React.FC = () => {
	const [chatValue, setChatValue] = React.useState('');
	const [previewValue, setPreviewValue] = React.useState('');
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	const handleColdStartButtonClick = (prompt: string) => {
		setChatValue(prompt);
		setPreviewValue(''); // Clear preview when button is clicked
	};

	const handleColdStartButtonHover = (prompt: string) => {
		// Only show preview if there's no current value
		if (!chatValue) {
			setPreviewValue(prompt);
		}
	};

	const handleColdStartButtonHoverEnd = () => {
		setPreviewValue('');
	};

	// Auto-focus textarea on mount
	React.useEffect(() => {
		textareaRef.current?.focus({ preventScroll: true });
	}, []);

	return (
		<div css={chatBoxContainerStyles}>
			<RovoChatBox
				textareaRef={textareaRef}
				value={chatValue}
				onValueChange={setChatValue}
				previewValue={previewValue}
			/>
			<ColdStartButtonsRow
				onButtonClick={handleColdStartButtonClick}
				onButtonHover={handleColdStartButtonHover}
				onButtonHoverEnd={handleColdStartButtonHoverEnd}
			/>
			<AIFooter />
		</div>
	);
};
/**
 * RovoChat component displays a welcoming header for the Rovo chat experience - will add other components of the chat box in future PRs
 */
export const RovoChat: React.FC = () => {
	return (
		<div css={sectionStyles}>
			<ChatHeader />
			<RovoChatBoxContainer />
		</div>
	);
};
